import { CalendarDate } from '@cabal/shared/api-search/values/calendar-date.js';
import { GanttDateFormat } from './gantt-types';
import { toStartOfUtcDay, createUtcDate } from './gantt-date-core';

const MILLIS_PER_SECOND = 1000;
const MILLIS_PER_MILLISECOND = 1;
const INTEGER_TOKEN_REGEX = /^[+-]?\d+$/;

function parseCalendarDate(value: string): Date | null {
  const admitted = CalendarDate.admit(value);
  return admitted.admitted ? createUtcDate(admitted.value) : null;
}

// Mermaid `dateFormat X` (seconds) / `x` (milliseconds): the schedule token is a
// bare Unix timestamp (e.g. `0`). Floor to the UTC day so it shares the same
// whole-day granularity as every other date in the layout engine.
function parseUnixTimestampDate(value: string, millisPerUnit: number): Date | null {
  if (!INTEGER_TOKEN_REGEX.test(value)) {
    return null;
  }

  const units = Number(value);
  if (!Number.isFinite(units)) {
    return null;
  }

  const date = new Date(units * millisPerUnit);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return toStartOfUtcDay(date);
}

const GANTT_DATE_PARSER_BY_FORMAT: Record<GanttDateFormat, (value: string) => Date | null> = {
  [GanttDateFormat.IsoDate]: parseCalendarDate,
  [GanttDateFormat.UnixSeconds]: (value) => parseUnixTimestampDate(value, MILLIS_PER_SECOND),
  [GanttDateFormat.UnixMillis]: (value) => parseUnixTimestampDate(value, MILLIS_PER_MILLISECOND),
};

export function parseGanttDate(dateString: string, format: string): Date | null {
  const parser = Object.hasOwn(GANTT_DATE_PARSER_BY_FORMAT, format)
    ? GANTT_DATE_PARSER_BY_FORMAT[format as GanttDateFormat] : parseCalendarDate;
  return parser(dateString.trim());
}
